import React from 'react';

import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/TableConstants';
import {ChipSize} from 'ui-library/chips/Chip';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import ChipSelectInput from 'ui-library/inputs/ChipSelectInput';
import {OptionData} from 'ui-library/menus/MenuConstants';
import {ElementKeyboardEvent, InputChangeEvent} from 'util/Types';

import {UnitTypesQuery} from './__generated/UnitTypesQuery';
import {getSortedMatches, getUnitOptions} from './SearchableUnitDropdown';
import {UNIT_TYPES_QUERY} from './UnitsConstants';
import {HydratedUnit} from './util';

interface Props {
  query: string;
  typedValue: TypedValue | null;
  onKeyDown: (e: ElementKeyboardEvent) => void;
  onQueryChange?: (e: InputChangeEvent) => void;
  updateTypedValue: (typedValue: TypedValue | null) => void;
  unitTypesFilterPredicate?: (unitType: object) => boolean;
  unitTypeId?: string;
}

function getUnitFromTypedValue(
  typedValue: TypedValue | null,
  options: Array<OptionData>
): HydratedUnit | undefined {
  if (!typedValue || typedValue.type !== BaseTypedValueType.UNIT_REFERENCE) {
    return undefined;
  }

  const allUnits = options.filter((o) => o.value).map((o) => o.value);
  return allUnits.find((u) => u.id === typedValue.unitSummary.id);
}

export default function UnitReferenceCellInput(props: Props): JSX.Element {
  return (
    <BenchlingQuery<UnitTypesQuery>
      query={UNIT_TYPES_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
    >
      {({unitTypes}, {refetch}) => {
        const options = getUnitOptions(unitTypes, {
          unitTypeFilter: props.unitTypesFilterPredicate,
          unitTypeId: props.unitTypeId,
        });
        const matchingUnitType = props.unitTypeId
          ? unitTypes.find((unitType) => unitType.id === props.unitTypeId)
          : undefined;
        const placeholder = matchingUnitType ? `${matchingUnitType.name} unit` : '';
        const matchingOptions = getSortedMatches(props.query, options);
        const value = getUnitFromTypedValue(props.typedValue, options);
        return (
          <ChipSelectInput
            placeholder={placeholder}
            optionDatas={value ? [] : matchingOptions}
            query={props.query}
            value={value ? [value] : []}
            onKeyDown={props.onKeyDown}
            onQueryChange={props.onQueryChange}
            scrollable={true}
            disableTyping={Boolean(value)}
            getChipProps={(value: HydratedUnit) => ({
              text: `${value.name} (${value.symbol})`,
              size: ChipSize.TABLE_CELL,
              icon: 'ruler',
            })}
            onSelect={(selected: HydratedUnit | null): void => {
              props.updateTypedValue(
                selected && {
                  type: BaseTypedValueType.UNIT_REFERENCE,
                  unitSummary: {id: selected.id, name: selected.name, symbol: selected.symbol},
                }
              );
            }}
            onRemove={() => {
              props.updateTypedValue(null);
            }}
          />
        );
      }}
    </BenchlingQuery>
  );
}
